import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { ApiError } from '../../../lib/http/ApiError';
import { AppModal } from '../../../components/ui/AppModal';
import { useNotification } from '../../notifications/hooks/useNotification';
import { usePermissions } from '../../permissions/usePermissions';
import { useAuth } from '../../auth/hooks/useAuth';
import { useGetUser } from '../hooks/useGetUser';
import { useUpdateUser } from '../hooks/useUpdateUser';
import { useAssignUserProfile } from '../hooks/useAssignUserProfile';
import { useDeleteUser } from '../hooks/useDeleteUser';
import { ResetPasswordDialog } from '../components/UserActionDialogs/ResetPasswordDialog';
import { isAwaitingActivation } from '../utils/awaitingActivation';
import { canSendPasswordLink } from '../utils/passwordLinkAccess';
import {
  UserForm,
  type UserFormInitialValues,
  type UserFormSubmit,
} from '../components/UserForm/UserForm';
import type { UpdateUserPayload } from '../userTypes';

const USERS_LIST = '/my-ngo/users';

/**
 * Edit page (HOMCO-623). The route is gated on `users.display`, so a caller without
 * `users.modify` sees the fiche read-only. Role editing is admin-only (HOMCO-1376,
 * admin → users downgrade: role + profile_id in ONE PATCH, admin caller only). Save
 * ordering mirrors the backend rules:
 * - reactivation of a profile-less `users` account (no role change): PUT profile FIRST,
 *   then PATCH is_active;
 * - otherwise: ONE PATCH with the changed fields (incl. role, and the new profile_id on
 *   a demotion), then PUT the profile only if it changed WITHOUT a role change.
 * Username and e-mail are immutable (read-only). Block C sends a single-use password
 * link (POST /users/:id/password-link, HOMCO-1393) — admin only, never on one's own
 * fiche; « Renvoyer le lien d'activation » while the account has never signed in.
 * Delete lives in the header (hidden on one's own fiche, disabled while active).
 */
export function EditUserPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('users');
  const navigate = useNavigate();
  const { notifySuccess } = useNotification();
  const { can, refreshPermissions } = usePermissions();
  const { isInitialized, username: callerUsername, role: callerRole } = useAuth();

  const { data: user, loading: userLoading, error: userError, refetch } = useGetUser(id);
  const { updateUser, loading: updating, error: updateError } = useUpdateUser();
  const { assignUserProfile, loading: assigning, error: assignError } = useAssignUserProfile();
  const { deleteUser, loading: deleting, error: deleteError } = useDeleteUser();

  const [confirmDelete, setConfirmDelete] = useState(false);
  // Password link (block C): confirmation + call live in the shared dialog.
  const [confirmReset, setConfirmReset] = useState(false);
  // Bumped after a partial failure to remount the form on the refetched baseline.
  const [formKey, setFormKey] = useState(0);
  // Which mutation(s) the latest action attempted — so a stale error from a hook that
  // was NOT called this time can't mask the real one (each hook keeps its last error).
  const [attempted, setAttempted] = useState({
    update: false,
    assign: false,
    delete: false,
    roleChange: false,
  });

  const readOnly = isInitialized && !can('users', 'modify');
  const isSelf = Boolean(user && callerUsername && user.username === callerUsername);

  // The mutation hooks swallow 4xx (returning null/false) and only expose them via
  // `error`; 5xx were toasted by the hooks. Only consider the errors of the calls the
  // latest action actually attempted, then map them to the relevant surface.
  const backendError =
    (attempted.update ? updateError : null) ??
    (attempted.assign ? assignError : null) ??
    (attempted.delete ? deleteError : null);
  let usernameError: string | undefined;
  let emailError: string | undefined;
  let profileError: string | undefined;
  let roleError: string | undefined;
  let formError: string | undefined;
  if (backendError && ApiError.isApiError(backendError)) {
    const message = (backendError.messages[0] ?? '').toLowerCase();
    const isLastAdmin =
      backendError.statusCode === 409 && message.includes('at least one active admin');
    if (backendError.statusCode === 409 && message.includes('username')) {
      usernameError = t('form.usernameConflict');
    } else if (backendError.statusCode === 409 && message.includes('email')) {
      emailError = t('form.emailConflict');
    // Match the real backend messages: UserProfileRequiredException → « Un profil de
    // droits doit être attribué… » ('profil' is also a substring of 'profile'), and
    // SelfPermissionEscalationException → « You cannot grant yourself a permission… ».
    } else if (backendError.statusCode === 400 && message.includes('profil')) {
      // A demotion without profile and a reactivation without profile are both 400+profil;
      // the front requires a profile in both, so this is a belt-and-braces message (F6).
      profileError = attempted.roleChange
        ? t('form.profileRequiredOnDemotion')
        : t('form.profileRequired');
    } else if (
      backendError.statusCode === 403 &&
      (message.includes('profil') || message.includes('escalat') || message.includes('permission'))
    ) {
      profileError = t('form.selfEscalation');
    } else if (backendError.statusCode === 403) {
      // e.g. UserSelfRoleChangeForbidden — should not happen (role locked on one's own
      // fiche), so a generic insufficient-rights message (F6).
      formError = t('form.forbidden');
    } else if (backendError.statusCode === 404) {
      formError = t('form.profileNotFound');
    } else if (isLastAdmin && attempted.roleChange) {
      // Demoting the last active admin → under the Role field (F6), not a form alert.
      roleError = t('form.lastAdminDemotion');
    } else if (isLastAdmin) {
      // Deactivating the last active admin (no role change).
      formError = t('delete.lastAdmin');
    } else if (backendError.statusCode === 409 && message.includes('active')) {
      formError = t('delete.activeError');
    } else if (backendError.isClientError) {
      console.error('[EditUserPage] save failed:', backendError.messages);
      formError = t('form.saveFailed');
    }
  }

  const handleSubmit = async (values: UserFormSubmit) => {
    if (!user) return;

    const roleChanged = values.role !== user.role;
    // A demotion admin/hq → users carries the new profile in the SAME PATCH (HOMCO-1254
    // /1376 F4); never a separate PUT /profile.
    const isDemotion = roleChanged && values.role === 'users';
    // A separate PUT /profile only applies to an account that STAYS `users` (no role
    // change): reactivation of a profile-less one, or a plain profile change.
    const stayingUsers = !roleChanged && user.role === 'users';
    const reactivationWithoutProfile =
      !user.is_active &&
      !user.profile_id &&
      values.is_active &&
      stayingUsers &&
      Boolean(values.profile_id);
    const profileChangedSameRole =
      stayingUsers && !reactivationWithoutProfile && values.profile_id !== (user.profile_id ?? '');
    const profileWillChange = reactivationWithoutProfile || profileChangedSameRole;

    const patch: UpdateUserPayload = {};
    if (values.display_name !== (user.display_name ?? '')) patch.display_name = values.display_name;
    if (values.is_active !== user.is_active) patch.is_active = values.is_active;
    if (values.perimeterChanged) patch.perimeter_nodes = values.perimeter_nodes;
    if (roleChanged) patch.role = values.role;
    if (isDemotion) patch.profile_id = values.profile_id;

    // Record which calls this submit will make, so error mapping ignores stale errors.
    // In the reactivation path the PATCH runs AFTER the PUT, so it is marked attempted
    // only when it actually starts (below) — otherwise a failed profile-first PUT would
    // surface a stale PATCH error instead of its own.
    setAttempted({
      update: !reactivationWithoutProfile && Object.keys(patch).length > 0,
      assign: profileWillChange,
      delete: false,
      roleChange: roleChanged,
    });

    let ok = true;
    let anyWrite = false; // at least one mutation persisted

    if (reactivationWithoutProfile) {
      // The backend refuses reactivation of a profile-less account, so set the
      // profile FIRST, then flip is_active (+ any other change) via PATCH.
      ok = Boolean(await assignUserProfile(user.id, values.profile_id));
      if (ok) anyWrite = true;
      if (ok && Object.keys(patch).length > 0) {
        setAttempted({ update: true, assign: true, delete: false, roleChange: false });
        ok = Boolean(await updateUser(user.id, patch));
      }
    } else {
      if (Object.keys(patch).length > 0) {
        ok = Boolean(await updateUser(user.id, patch));
        if (ok) anyWrite = true;
      }
      if (ok && profileChangedSameRole) {
        ok = Boolean(await assignUserProfile(user.id, values.profile_id));
      }
    }

    if (!ok) {
      // Partial failure: a first write may already be persisted while a later one
      // failed. Re-sync the form to the backend so a retry compares against the real
      // state instead of silently dropping the already-applied change.
      if (anyWrite) {
        refetch();
        setFormKey(key => key + 1);
      }
      return; // the error is surfaced from the hooks' `error`
    }

    // Changing one's OWN rights profile changes one's permissions — refresh the cached
    // permissions before leaving, so revoked screens/actions are not left enabled.
    if (isSelf && profileWillChange) {
      await refreshPermissions();
    }
    // A3 (HOMCO-1376): a role change closes the account's sessions (HOMCO-1184), so the
    // success confirmation adds the reconnection notice; an unchanged role keeps the
    // plain edit.success text.
    notifySuccess(
      roleChanged ? `${t('edit.success')} ${t('role.reconnectNotice')}` : t('edit.success'),
    );
    navigate(USERS_LIST);
  };

  const handleDelete = async () => {
    if (!user) return;
    setAttempted({ update: false, assign: false, delete: true, roleChange: false });
    const result = await deleteUser(user.id);
    setConfirmDelete(false);
    if (result === true) {
      notifySuccess(t('delete.success'));
      navigate(USERS_LIST);
    }
  };

  // `!user && !userError` is the pre-fetch tick (load hasn't started yet) — treat it as
  // loading, not as a load error, to avoid flashing the error alert on first render.
  if (!isInitialized || userLoading || (!user && !userError)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userError || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('edit.loadError')}</Alert>
      </Box>
    );
  }

  const initialValues: UserFormInitialValues = {
    username: user.username,
    display_name: user.display_name ?? '',
    email: user.email,
    is_active: user.is_active,
    role: user.role,
    profile_id: user.profile_id ?? '',
    perimeter_nodes: user.perimeter_nodes,
  };

  const showDelete = can('users', 'delete') && !isSelf;
  // Same rule as the list row menu; an inactive account keeps the button, disabled.
  const showPasswordLink = canSendPasswordLink({
    callerRole,
    callerUsername,
    canModify: !readOnly,
    target: user,
  });

  return (
    <>
      <UserForm
        key={formKey}
        mode="edit"
        title={t('edit.title', { name: user.display_name ?? user.username })}
        subtitle={t('edit.subtitle')}
        submitLabel={t('form.save')}
        initialValues={initialValues}
        readOnly={readOnly}
        isSelf={isSelf}
        callerRole={callerRole ?? undefined}
        usernameError={usernameError}
        emailError={emailError}
        profileError={profileError}
        roleError={roleError}
        profileName={user.profile_name ?? ''}
        formError={formError}
        saving={updating || assigning}
        showDelete={showDelete}
        onDelete={() => setConfirmDelete(true)}
        deleteDisabled={user.is_active}
        deleteTooltip={t('delete.tooltipActive')}
        onResetPassword={showPasswordLink ? () => setConfirmReset(true) : undefined}
        resetLabel={t(isAwaitingActivation(user) ? 'reset.resendActivation' : 'reset.button')}
        resetHint={t(isAwaitingActivation(user) ? 'reset.activationHint' : 'reset.hint')}
        onSubmit={handleSubmit}
        onCancel={() => navigate(USERS_LIST)}
      />

      <AppModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        titleId="uf-delete-title"
        icon={<WarningAmberOutlinedIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
        title={t('delete.confirmTitle')}
        closeLabel={t('delete.cancel')}
      >
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            {t('delete.confirmMessage', { name: user.display_name ?? user.username })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setConfirmDelete(false)}
            disabled={deleting}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('delete.cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ borderRadius: 5, textTransform: 'none' }}
          >
            {t('delete.confirm')}
          </Button>
        </DialogActions>
      </AppModal>

      {confirmReset && (
        <ResetPasswordDialog
          user={user}
          onClose={() => setConfirmReset(false)}
          onSuccess={refetch}
        />
      )}
    </>
  );
}
